import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { api, exigir, ErroApi } from "../api/cliente";
import type { components } from "../api/esquema";
import { Botao } from "../componentes/kit/Botao";
import { CabecalhoTela } from "../componentes/kit/CabecalhoTela";
import { Campo, Selecao } from "../componentes/kit/Campo";
import { Cartao } from "../componentes/kit/Cartao";
import { Falha } from "../componentes/kit/Estados";
import { Dados } from "../componentes/kit/PainelLateral";
import { diaDaSemana, formatarData } from "../util/datas";

type Contagem = components["schemas"]["Contagem"];

/** Primeira peça do lançamento do controller (ticket E4-05): a prévia do prazo. Quem calcula é a API (§5.1);
 *  a tela só mostra. */
export function Lancamentos() {
  const [intimacao, setIntimacao] = useState("");
  const [dias, setDias] = useState(15);
  const [contagem, setContagem] = useState<Contagem>("uteis");

  const calculo = useMutation({
    mutationFn: () =>
      exigir(api.POST("/api/prazos/calcular", {
        body: { data_intimacao: intimacao, dias, contagem, tribunal: "TJRS", uf: "RS", municipio: "Caxias do Sul" },
      })),
  });

  function enviar(e: FormEvent) {
    e.preventDefault();
    calculo.mutate();
  }

  const r = calculo.data;
  return (
    <div className="mx-auto max-w-3xl">
      <CabecalhoTela titulo="Lançamentos" contexto="Prévia do prazo. A escolha do processo e da tarefa-padrão chega no E4-05." />

      <form onSubmit={enviar} className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_7rem_1fr_auto]">
        <Campo rotulo="Intimação" type="date" required value={intimacao} onChange={(e) => setIntimacao(e.target.value)} />
        <Campo rotulo="Dias" type="number" min={1} max={365} required value={dias} onChange={(e) => setDias(Number(e.target.value))} />
        <Selecao rotulo="Contagem" value={contagem} onChange={(e) => setContagem(e.target.value as Contagem)}>
          <option value="uteis">dias úteis</option>
          <option value="corridos">dias corridos</option>
        </Selecao>
        <Botao type="submit" variante="primario" grande carregando={calculo.isPending}>Calcular</Botao>
      </form>

      {calculo.error && (
        <div className="mt-4">
          <Falha mensagem={calculo.error instanceof ErroApi ? calculo.error.message : "Não foi possível calcular."} />
        </div>
      )}

      {r && (
        <Cartao titulo="Resultado" className="mt-6">
          <Dados itens={[
            { rotulo: "Execução (D-1 útil)", valor: <span className="numeros font-medium">{formatarData(r.execucao_sugerida)} ({diaDaSemana(r.execucao_sugerida)})</span> },
            { rotulo: "Prazo fatal", valor: <span className="numeros">{formatarData(r.fatal)} ({diaDaSemana(r.fatal)})</span> },
            {
              rotulo: "Considerado",
              valor: r.dias_pulados.length === 0 ? "nenhum feriado no período" : (
                <ul className="flex flex-col gap-0.5">
                  {r.dias_pulados.map((p) => (
                    <li key={p.data}>{p.motivo}: <span className="numeros">{formatarData(p.data)}{p.ate ? ` a ${formatarData(p.ate)}` : ""}</span></li>
                  ))}
                </ul>
              ),
            },
            { rotulo: "Regra", valor: <span className="font-mono text-meta text-texto-3">{r.regra_versao}</span> },
          ]} />
        </Cartao>
      )}
    </div>
  );
}
